/*
 * TaskList is a component used to retrieve and display a list of tasks.  It is used within the CustomerTasks component.
 */
import React, { Component } from 'react';
import { QueryRenderer } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';

import { Grid, Typography } from '@mui/material';
import AbEnvironment from '../auth/AbEnvironment';
import ServerError from '../ui/ServerError';
import LoadingSpinner from '../ui/LoadingSpinner';
import TaskRibbon from './TaskRibbon'
import UpdateFlowStepMutation from './UpdateFlowStepMutation'

const abEnvironment = new AbEnvironment();

const TaskListQuery = graphql`
query TaskListQuery($accountId: UUID!) {
  allFlowTs(condition:{assignedAccountId:$accountId}) {
    totalCount
    nodes {
      accountTByRequestorAccountId {
        accountName
      }
      title
      description
      flowStepTsByFlowId(orderBy:STEP_ORDER_ASC) {
        totalCount
        nodes {
          flowStepId
          stepOrder
          stepState
          flowTaskId
          flowTaskTByFlowTaskId {
            flowTaskId
            assignedAccountId
            taskType
            taskData
            title
            instruction
            linkName
            linkUrl
            customerAppId
            customerAppTByCustomerAppId {
              customerAppId
              customerAccountId
              provisioningAccountId
              accountTByProvisioningAccountId {
                accountName
              }
              appId
              appStatus
              sharedEmail
              useEmail
              domain
              username
              pwd
              appTByAppId {
                appName
                appLogo
                showDomain
                showUsername
              }
            }
            ccId
            ccOnFileTByCcId {
              ccBrand
              ccExpy
              ccLast4
              firstName
              lastName
            }
          }
        }
      }
    }
  }
}`;

const styleSheet = theme => ({
  tblHead: {
    backgroundColor: '#f1f1f1',
  },
  tblHeadRow: {
    height: 24,
  },
  nonPaperContainer: {
    marginTop: 8
  }
});

class TaskList extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      forceFetch: 0
    };  
  }

  goTo(route) {
    this.props.history.push(`/${route}`);
  }

  markStepDone = (stepId) => {
    Promise.resolve()  
    .then(() => {  
      // console.log('markStepDone: stepId=' + stepId);
      return UpdateFlowStepMutation.commit(stepId, 1);
    }).then((response) => {
      // console.log('markStepDone forcing refetch');
      this.setState({forceFetch: this.state.forceFetch + 1});
    })
    .catch(error => {
      // console.log('Got error: ' + error);
    });
  }

  markStepPending = (stepId) => {
    Promise.resolve()  
    .then(() => {  
      return UpdateFlowStepMutation.commit(stepId, 0);
    }).then((response) => {
      this.setState({forceFetch: this.state.forceFetch + 1});
    })
    .catch(error => {
      // console.log('Got error: ' + error);
    });
  }

  render() {
    const { logError } = this.props.auth;
    const classes = this.props.classes;

    let acctId = abEnvironment.getUserAttribute("a_id");

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={TaskListQuery}
        variables={{accountId: acctId, forceFetch: this.state.forceFetch}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
                <ServerError/>
            );
          } else if (props) {
            // Tasks will be per flow, which is per project.  But initially, we'll just have one, so
            // take the first flow.
            let theNodes = props.allFlowTs.nodes;
            
            let toDoSteps = [];
            let completedSteps = [];
            let totalCount = 0;

            let theTitle = 'Onboarding tasks';
            let requestor = 'Your agent';
            let theSubTitle = "You don't have any onboarding tasks.";
            if (theNodes.length > 0) {
              let theFlow = theNodes[0];
              theTitle = theFlow.title;
              requestor = theFlow.accountTByRequestorAccountId.accountName;
              // theSubTitle = theFlow.description;

              let theFlowSteps = theFlow.flowStepTsByFlowId.nodes;
              if (theFlowSteps.length > 0) {
                for (let i=0; i<theFlowSteps.length; i++) {
                  let theFlowStep = theFlowSteps[i];
                  if (theFlowStep.stepState === 0) {
                    toDoSteps.push(theFlowStep);
                  } else if (theFlowStep.stepState === 1) {
                    completedSteps.push(theFlowStep);
                  }
                }
              }

              totalCount = theFlowSteps.length;
              let doneCount = completedSteps.length;
              theSubTitle = 'Completed ' + doneCount + ' of ' + totalCount + ' tasks';
            }

            return (
              <Grid container spacing={3} direction="row" justifyContent="flex-start">
                {this.props.parentMainTitle === true &&
                <Grid item xs={12}>
                  <Typography variant="h5">{theTitle}</Typography>
                  <Typography variant="subtitle1">{theSubTitle}</Typography>
                </Grid>}
                
                {this.props.parentMainTitle === false &&
                <Grid item xs={12}>
                  <Typography variant="h4">{theTitle}</Typography>
                  <Typography variant="subtitle1">{theSubTitle}</Typography>
                </Grid>}

                {toDoSteps.length > 0 &&
                <Grid item xs={12}>
                  <Grid container spacing={4} direction="row" justifyContent="flex-start">
                    { toDoSteps.map(n => {
                      let theStepId = n.flowStepId;
                      let theStepOrder = n.stepOrder;
                      let theTask = n.flowTaskTByFlowTaskId;

                      return (
                        <Grid item xs={12} key={n.flowStepId}>
                          <TaskRibbon
                            auth={this.props.auth}
                            stepId={theStepId}
                            stepOrder={theStepOrder}
                            requestor={requestor}
                            task={theTask}
                            done={false}
                            completeTask={this.markStepDone}
                            undoTask={this.markStepPending}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>}
                {completedSteps.length > 0 &&
                <React.Fragment>
                  {toDoSteps.length > 0 &&
                  <Grid item xs={12}>
                    <Typography variant="h6">Done</Typography>
                  </Grid>}
                  <Grid item xs={12}>
                    <Grid container spacing={2} direction="row" justifyContent="flex-start">
                      { completedSteps.map(n => {
                        let theStepId = n.flowStepId;
                        let theStepOrder = n.stepOrder;
                        let theTask = n.flowTaskTByFlowTaskId;

                        return (
                          <Grid item xs={12} key={n.flowStepId}>
                            <TaskRibbon 
                              auth={this.props.auth}
                              stepId={theStepId}
                              stepOrder={theStepOrder}
                              requestor={requestor}
                              task={theTask}
                              done={true}
                              flatten={true}
                              completeTask={this.markStepDone}
                              undoTask={this.markStepPending}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                </React.Fragment>}
              </Grid>
            );
          }

          return (
            <div>
              <Typography>Loading...</Typography>
              <LoadingSpinner/>
            </div>
          );
        }}
      />
    );
  }
}

TaskList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(TaskList);